import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  infoGrid: {
    marginBottom: theme.spacing(2),
  },
  infoLabel: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  infoValue: {
    fontWeight: 'normal',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  amountPositive: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  amountNegative: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  statusSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
}));

const TransactionDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [relatedData, setRelatedData] = useState({
    booking: null,
    user: null,
    partner: null
  });

  useEffect(() => {
    fetchTransaction();
  }, [id]);

  const fetchTransaction = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const transactionDoc = doc(firestore, 'transactions', id);
      const transactionSnapshot = await getDoc(transactionDoc);
      
      if (transactionSnapshot.exists()) {
        const transactionData = {
          id: transactionSnapshot.id,
          ...transactionSnapshot.data()
        };
        setTransaction(transactionData);
        fetchRelatedData(transactionData);
      } else {
        // Si la transaction n'existe pas, utiliser des données de démonstration
        if (id === 'TRX001') {
          const demoTransaction = { 
            id: 'TRX001', 
            reference: 'PAY-2025-001',
            type: 'payment',
            bookingId: 'B001',
            bookingReference: 'RES-2025-001',
            userId: 'U001',
            userName: 'Alice Martin',
            amount: 120.50,
            currency: 'EUR',
            status: 'completed',
            paymentMethod: 'card',
            cardLast4: '4242',
            description: 'Paiement pour réservation Paris-Lyon',
            createdAt: '2025-03-15T10:30:00',
            updatedAt: '2025-03-15T10:30:00'
          };
          setTransaction(demoTransaction);
          fetchRelatedData(demoTransaction);
        } else if (id === 'TRX002') {
          const demoTransaction = { 
            id: 'TRX002', 
            reference: 'REF-2025-001',
            type: 'refund',
            bookingId: 'B005',
            bookingReference: 'RES-2025-005',
            userId: 'U005',
            userName: 'Julie Moreau',
            amount: 65.30,
            currency: 'EUR',
            status: 'completed',
            paymentMethod: 'card',
            cardLast4: '1234',
            description: 'Remboursement pour annulation Lille-Paris',
            createdAt: '2025-03-19T14:25:00',
            updatedAt: '2025-03-19T14:25:00'
          };
          setTransaction(demoTransaction);
          fetchRelatedData(demoTransaction);
        } else if (id === 'TRX003') {
          const demoTransaction = { 
            id: 'TRX003', 
            reference: 'PAY-2025-002',
            type: 'payment',
            bookingId: 'B002',
            bookingReference: 'RES-2025-002',
            userId: 'U002',
            userName: 'Thomas Bernard',
            amount: 85.75,
            currency: 'EUR',
            status: 'completed',
            paymentMethod: 'paypal',
            description: 'Paiement pour réservation Marseille-Nice',
            createdAt: '2025-03-10T14:45:00',
            updatedAt: '2025-03-10T14:45:00'
          };
          setTransaction(demoTransaction);
          fetchRelatedData(demoTransaction);
        } else if (id === 'TRX004') {
          const demoTransaction = { 
            id: 'TRX004', 
            reference: 'PAY-2025-003',
            type: 'payment',
            bookingId: 'B003',
            bookingReference: 'RES-2025-003',
            userId: 'U003',
            userName: 'Sophie Dubois',
            amount: 145.20,
            currency: 'EUR',
            status: 'pending',
            paymentMethod: 'bank_transfer',
            description: 'Paiement en attente pour réservation Lyon-Grenoble',
            createdAt: '2025-04-02T11:15:00',
            updatedAt: '2025-04-02T11:15:00'
          };
          setTransaction(demoTransaction);
          fetchRelatedData(demoTransaction);
        } else if (id === 'TRX005') {
          const demoTransaction = { 
            id: 'TRX005', 
            reference: 'COM-2025-001',
            type: 'commission_payment',
            partnerId: 'P001',
            partnerName: 'Entreprise ABC',
            amount: 45.50,
            currency: 'EUR',
            status: 'completed',
            paymentMethod: 'bank_transfer',
            description: 'Paiement de commission pour partenaire',
            createdAt: '2025-03-20T09:30:00',
            updatedAt: '2025-03-20T09:30:00'
          };
          setTransaction(demoTransaction);
          fetchRelatedData(demoTransaction);
        } else {
          setSnackbar({
            open: true,
            message: 'Transaction non trouvée',
            severity: 'error'
          });
          setTimeout(() => {
            navigate('/admin/transactions');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error fetching transaction:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de la transaction',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedData = async (transactionData) => {
    try {
      // Simulation de récupération des données associées
      // Dans une implémentation réelle, cela serait remplacé par des appels à l'API
      
      if (transactionData.type === 'payment' || transactionData.type === 'refund') {
        // Données de démonstration pour la réservation
        if (transactionData.bookingId) {
          const demoBooking = {
            id: transactionData.bookingId,
            reference: transactionData.bookingReference,
            userId: transactionData.userId,
            userName: transactionData.userName,
            tripId: 'T001',
            tripReference: 'VOY-2025-001',
            departureDate: '2025-04-15T08:00:00',
            arrivalDate: '2025-04-15T10:30:00',
            departureLocation: 'Paris',
            arrivalLocation: 'Lyon',
            status: 'confirmed',
            amount: transactionData.amount,
            currency: transactionData.currency,
            createdAt: '2025-03-10T14:30:00'
          };
          setRelatedData(prev => ({ ...prev, booking: demoBooking }));
        }
        
        // Données de démonstration pour l'utilisateur
        if (transactionData.userId) {
          const demoUser = {
            id: transactionData.userId,
            name: transactionData.userName,
            email: `${transactionData.userName.toLowerCase().replace(' ', '.')}@example.com`,
            phone: '+33 6 12 34 56 78',
            createdAt: '2025-01-15T10:00:00',
            status: 'active'
          };
          setRelatedData(prev => ({ ...prev, user: demoUser }));
        }
      } else if (transactionData.type === 'commission_payment') {
        // Données de démonstration pour le partenaire
        if (transactionData.partnerId) {
          const demoPartner = {
            id: transactionData.partnerId,
            name: transactionData.partnerName,
            email: `contact@${transactionData.partnerName.toLowerCase().replace(' ', '')}.com`,
            phone: '+33 1 23 45 67 89',
            address: '123 Avenue des Partenaires, 75001 Paris',
            createdAt: '2024-12-01T09:00:00',
            status: 'active'
          };
          setRelatedData(prev => ({ ...prev, partner: demoPartner }));
        }
      }
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  };

  const handleBackToList = () => {
    navigate('/admin/transactions');
  };

  const handleEdit = () => {
    navigate(`/admin/transactions/${id}/edit`);
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'transactions', id));
      
      setSnackbar({
        open: true,
        message: 'Transaction supprimée avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste des transactions après un court délai
      setTimeout(() => {
        navigate('/admin/transactions');
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression de la transaction',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleExportReceipt = () => {
    // Logique d'exportation du reçu
    // Dans une implémentation réelle, cela générerait un PDF
    setSnackbar({
      open: true,
      message: 'Génération du reçu en cours...',
      severity: 'info'
    });
    
    // Simulation d'un délai pour l'export
    setTimeout(() => {
      setSnackbar({
        open: true,
        message: 'Reçu généré avec succès',
        severity: 'success'
      });
    }, 1500);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'pending':
        return 'primary';
      case 'failed':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Terminée';
      case 'pending':
        return 'En attente';
      case 'failed':
        return 'Échouée';
      default:
        return status;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'payment':
        return 'Paiement';
      case 'refund':
        return 'Remboursement';
      case 'commission_payment':
        return 'Paiement commission';
      default:
        return type;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'card':
        return 'Carte bancaire';
      case 'paypal':
        return 'PayPal';
      case 'bank_transfer':
        return 'Virement bancaire';
      default:
        return method;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!transaction) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          Transaction non trouvée
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
          style={{ marginTop: 16 }}
        >
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToList}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <div className={classes.statusSection}>
        <Typography variant="h4" component="h1" className={classes.title}>
          Transaction {transaction.reference}
        </Typography>
        <Chip 
          label={getStatusLabel(transaction.status)} 
          color={getStatusColor(transaction.status)} 
          className={classes.chip}
        />
      </div>
      
      <Paper className={classes.paper}>
        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Informations générales
          </Typography>
          
          <Grid container spacing={2} className={classes.infoGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" className={classes.infoLabel}>
                Type de transaction
              </Typography>
              <Typography variant="body1" className={classes.infoValue}>
                {getTypeLabel(transaction.type)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" className={classes.infoLabel}>
                Référence
              </Typography>
              <Typography variant="body1" className={classes.infoValue}>
                {transaction.reference}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" className={classes.infoLabel}>
                Date de création
              </Typography>
              <Typography variant="body1" className={classes.infoValue}>
                {formatDate(transaction.createdAt)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" className={classes.infoLabel}>
                Dernière mise à jour
              </Typography>
              <Typography variant="body1" className={classes.infoValue}>
                {formatDate(transaction.updatedAt)}
              </Typography>
            </Grid>
          </Grid>
          
          <Grid container spacing={2} className={classes.infoGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" className={classes.infoLabel}>
                Montant
              </Typography>
              <Typography 
                variant="body1" 
                className={
                  transaction.type === 'payment' 
                    ? classes.amountPositive 
                    : classes.amountNegative
                }
              >
                {formatCurrency(transaction.amount, transaction.currency)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" className={classes.infoLabel}>
                Méthode de paiement
              </Typography>
              <Typography variant="body1" className={classes.infoValue}>
                {getPaymentMethodLabel(transaction.paymentMethod)}
                {transaction.cardLast4 && ` (****${transaction.cardLast4})`}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body2" className={classes.infoLabel}>
                Description
              </Typography>
              <Typography variant="body1" className={classes.infoValue}>
                {transaction.description}
              </Typography>
            </Grid>
          </Grid>
        </div>
        
        <Divider className={classes.divider} />
        
        {(transaction.type === 'payment' || transaction.type === 'refund') && (
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails de la réservation et du client
            </Typography>
            
            <Grid container spacing={2} className={classes.infoGrid}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" className={classes.infoLabel}>
                  Référence réservation
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  {transaction.bookingReference}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" className={classes.infoLabel}>
                  Client
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  {transaction.userName}
                </Typography>
              </Grid>
              
              {relatedData.booking && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" className={classes.infoLabel}>
                      Départ
                    </Typography>
                    <Typography variant="body1" className={classes.infoValue}>
                      {relatedData.booking.departureLocation}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" className={classes.infoLabel}>
                      Arrivée
                    </Typography>
                    <Typography variant="body1" className={classes.infoValue}>
                      {relatedData.booking.arrivalLocation}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
            
            {relatedData.user && (
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table aria-label="user details table">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID Client</TableCell>
                      <TableCell>Nom</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Téléphone</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{relatedData.user.id}</TableCell>
                      <TableCell>{relatedData.user.name}</TableCell>
                      <TableCell>{relatedData.user.email}</TableCell>
                      <TableCell>{relatedData.user.phone}</TableCell>
                      <TableCell>
                        <Chip 
                          label={relatedData.user.status} 
                          color={relatedData.user.status === 'active' ? 'primary' : 'default'} 
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}
        
        {transaction.type === 'commission_payment' && relatedData.partner && (
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails du partenaire
            </Typography>
            
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table aria-label="partner details table">
                <TableHead>
                  <TableRow>
                    <TableCell>ID Partenaire</TableCell>
                    <TableCell>Nom</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Téléphone</TableCell>
                    <TableCell>Adresse</TableCell>
                    <TableCell>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{relatedData.partner.id}</TableCell>
                    <TableCell>{relatedData.partner.name}</TableCell>
                    <TableCell>{relatedData.partner.email}</TableCell>
                    <TableCell>{relatedData.partner.phone}</TableCell>
                    <TableCell>{relatedData.partner.address}</TableCell>
                    <TableCell>
                      <Chip 
                        label={relatedData.partner.status} 
                        color={relatedData.partner.status === 'active' ? 'primary' : 'default'} 
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
        
        <div className={classes.actions}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExportReceipt}
            className={classes.button}
          >
            Exporter reçu
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            className={classes.button}
            disabled={transaction.status === 'completed'}
          >
            Modifier
          </Button>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            className={classes.button}
            disabled={transaction.status === 'completed'}
          >
            Supprimer
          </Button>
        </div>
      </Paper>
      
      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer définitivement la transaction <strong>{transaction.reference}</strong> ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionDetailPage;
